import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { migrationsApi } from '@/api/migrations';
import type { MigrationDetail, ExecuteMigrationResponse } from '@/types/migration';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Database,
  Code,
  School,
  Play,
  Check,
  X,
  AlertTriangle,
  Loader2,
  Calendar,
  Tag,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function MigrationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [migration, setMigration] = useState<MigrationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<ExecuteMigrationResponse | null>(null);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  useEffect(() => {
    if (id) {
      fetchMigration(parseInt(id));
    }
  }, [id]);

  const fetchMigration = async (migrationId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await migrationsApi.getOne(migrationId);
      setMigration(data);
    } catch (err) {
      setError('Erreur lors du chargement de la migration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!migration) return;

    try {
      setDeleting(true);
      await migrationsApi.delete(migration.id);
      navigate('/migrations');
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression de la migration');
      setShowDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleExecute = async () => {
    if (!migration) return;

    try {
      setExecuting(true);
      setError(null);
      const result = await migrationsApi.execute(migration.id);
      setExecutionResult(result);

      // Refresh migration data
      await fetchMigration(migration.id);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de l\'execution de la migration');
    } finally {
      setExecuting(false);
      setShowExecuteDialog(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getExecutionStatus = () => {
    if (!migration) return null;

    if (migration.passed) {
      return (
        <Badge variant="success" className="gap-1">
          <Check className="h-3 w-3" />
          Completement executee
        </Badge>
      );
    }
    if (migration.executedSchoolsCount > 0) {
      return (
        <Badge variant="warning" className="gap-1">
          <AlertTriangle className="h-3 w-3" />
          Partiellement executee ({migration.executedSchoolsCount}/{migration.totalSchoolsCount})
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="gap-1">
        <X className="h-3 w-3" />
        Non executee
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <Skeleton className="h-48" />
          <Skeleton className="h-48 md:col-span-2" />
        </div>
      </div>
    );
  }

  if (error || !migration) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/migrations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux migrations
        </Button>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error || 'Migration non trouvee'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to="/migrations">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                {migration.name}
              </h1>
              <p className="text-sm text-muted-foreground font-mono">
                Version {migration.version}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!migration.passed && (
            <>
              <Button
                variant="default"
                onClick={() => setShowExecuteDialog(true)}
                disabled={executing}
              >
                {executing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Execution...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Executer
                  </>
                )}
              </Button>
              <Button variant="outline" asChild>
                <Link to={`/migrations/${migration.id}/edit`}>
                  <Pencil className="h-4 w-4" />
                  Modifier
                </Link>
              </Button>
            </>
          )}
          {isAdmin && (
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          )}
        </div>
      </div>

      {/* Execution result */}
      {executionResult && (
        <div
          className={`rounded-lg border p-4 ${
            executionResult.success
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          <div className="flex items-center gap-2 font-semibold mb-2">
            {executionResult.success ? (
              <Check className="h-5 w-5" />
            ) : (
              <AlertTriangle className="h-5 w-5" />
            )}
            {executionResult.message}
          </div>
          {executionResult.results.done.length > 0 && (
            <div className="mb-2">
              <span className="text-sm font-medium">Reussies ({executionResult.results.done.length}):</span>
              <div className="text-sm mt-1 flex flex-wrap gap-1">
                {executionResult.results.done.slice(0, 10).map((r) => (
                  <Badge key={r.schoolId} variant="outline" className="text-green-600 border-green-300">
                    {r.schoolName}
                  </Badge>
                ))}
                {executionResult.results.done.length > 10 && (
                  <Badge variant="secondary">
                    +{executionResult.results.done.length - 10} autres
                  </Badge>
                )}
              </div>
            </div>
          )}
          {executionResult.results.failed.length > 0 && (
            <div>
              <span className="text-sm font-medium">Echouees ({executionResult.results.failed.length}):</span>
              <div className="text-sm mt-1 space-y-1">
                {executionResult.results.failed.map((r) => (
                  <div key={r.schoolId} className="flex items-center gap-2">
                    <Badge variant="outline" className="text-red-600 border-red-300">
                      {r.schoolName}
                    </Badge>
                    {r.message && (
                      <span className="text-xs text-red-500">{r.message}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        {/* Migration Info */}
        <Card>
          <CardHeader className="border-b border-border bg-muted/30">
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4 text-primary" />
              Informations
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div>
              <dt className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                <Tag className="h-3 w-3" />
                Version
              </dt>
              <dd className="mt-1">
                <Badge variant="outline" className="font-mono">
                  {migration.version}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Statut</dt>
              <dd className="mt-1">{getExecutionStatus()}</dd>
            </div>
            {migration.passedAt && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Date d'execution
                </dt>
                <dd className="mt-1 text-sm">{formatDate(migration.passedAt)}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Cree le
              </dt>
              <dd className="mt-1 text-sm">{formatDate(migration.createdAt)}</dd>
            </div>
          </CardContent>
        </Card>

        {/* Execution Status */}
        <Card className="md:col-span-2">
          <CardHeader className="border-b border-border bg-muted/30">
            <CardTitle className="flex items-center gap-2 text-base">
              <School className="h-4 w-4 text-primary" />
              Écoles ({migration.executedSchoolsCount}/{migration.totalSchoolsCount})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {migration.executedSchools.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Cette migration n'a pas encore été exécutée sur aucune école.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground mb-3">
                  Écoles sur lesquelles la migration a été exécutée :
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {migration.executedSchools.map((school) => (
                    <div
                      key={school.id}
                      className="flex items-center gap-2 p-2 rounded-lg border bg-muted/20 text-sm"
                    >
                      <Check className="h-3 w-3 text-green-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{school.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {school.code}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SQL Script */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="flex items-center gap-2 text-base">
            <Code className="h-4 w-4 text-primary" />
            Script SQL
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <pre className="bg-muted/50 border rounded-lg p-4 overflow-x-auto text-sm font-mono whitespace-pre-wrap">
            {migration.scriptSQL}
          </pre>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la migration ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer la migration{' '}
              <strong>{migration.name}</strong> (v{migration.version}) ?
              <br />
              <br />
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Execute confirmation dialog */}
      <AlertDialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Executer la migration ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vous etes sur le point d'executer la migration{' '}
              <strong>{migration.name}</strong> (v{migration.version}) sur{' '}
              <strong>toutes les écoles</strong>.
              <br />
              <br />
              Le script SQL suivant sera execute sur chaque base de donnees:
              <pre className="mt-2 bg-muted p-2 rounded text-xs font-mono max-h-32 overflow-y-auto">
                {migration.scriptSQL.substring(0, 500)}
                {migration.scriptSQL.length > 500 && '...'}
              </pre>
              <br />
              <strong className="text-red-600">
                Cette action est irreversible. Assurez-vous que le script a ete teste.
              </strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={executing}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExecute}
              disabled={executing}
              className="bg-primary hover:bg-primary/90"
            >
              {executing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Execution en cours...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Executer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
